/**
 * Checks if the number is within the valid range for latitudes,
 * which is -90 to 90 degrees.
 * Latitudes south of the equator are negative,
 * while latitudes north of the equator are positive.
 *
 * Latitudes must be finite numbers
 */
export const isLatitude = (lat: number) => {
  return Number.isFinite(lat) && Math.abs(lat) <= 90
}

/**
 * Checks if the number is within the valid range for longitudes.
 * Longitude is a measure of east-west position on the Earth's surface,
 * expressed in degrees.
 * The range of valid longitude values is from -180 degrees to 180 degrees,
 * with positive values representing degrees east of the Prime Meridian
 * and negative values representing degrees west of the Prime Meridian.
 *
 * Longitudes must be finite numbers
 */
export const isLongitude = (lng: number) => {
  return Number.isFinite(lng) && Math.abs(lng) <= 180
}

/**
 * Calculates the center of a given google.maps.LatLngBounds object
 * by taking the average of the latitude and longitude values
 * of the bounds' north-east and south-west corners.
 */
export const getCenterOfBounds = (
  ne: google.maps.LatLngLiteral,
  sw: google.maps.LatLngLiteral,
) => {
  return {
    lat: (ne.lat + sw.lat) / 2,
    lng: (ne.lng + sw.lng) / 2,
  }
}

/**
 * Creates a google.maps.LatLngBounds object from a google.maps.LatLngBoundsLiteral object
 * which is a set of cardinal points (i.e., north, south, east, and west).
 */
export const makeLatLngBoundsFromCardinals = (
  cardinals: google.maps.LatLngBoundsLiteral,
  offset = 0,
) => {
  const southWest = new google.maps.LatLng(
    cardinals.south - offset,
    cardinals.west - offset,
  )
  const northEast = new google.maps.LatLng(
    cardinals.north + offset,
    cardinals.east + offset,
  )

  return new google.maps.LatLngBounds(southWest, northEast)
}

export type Alignment =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'center'
  | 'topleft'
  | 'lefttop'
  | 'topright'
  | 'righttop'
  | 'bottomleft'
  | 'leftop'
  | 'bottomright'
  | 'rightbottom'

/**
 * Calculates the optimal position for a marker
 */
export const markerPosition = (
  alignment: Alignment,
  posPixel: google.maps.Point,
  el: HTMLElement,
) => {
  const alignments = {
    bottom: {
      x: posPixel.x - el.offsetWidth / 2,
      y: posPixel.y,
    },
    bottomleft: {
      x: posPixel.x - el.offsetWidth,
      y: posPixel.y,
    },
    bottomright: {
      x: posPixel.x,
      y: posPixel.y,
    },
    center: {
      x: posPixel.x - el.offsetWidth / 2,
      y: posPixel.y - el.offsetHeight / 2,
    },
    left: {
      x: posPixel.x - el.offsetWidth,
      y: posPixel.y - el.offsetHeight / 2,
    },
    leftop: {
      x: posPixel.x - el.offsetWidth,
      y: posPixel.y,
    },
    lefttop: {
      x: posPixel.x - el.offsetWidth,
      y: posPixel.y - el.offsetHeight,
    },
    right: {
      x: posPixel.x,
      y: posPixel.y - el.offsetHeight / 2,
    },
    rightbottom: {
      x: posPixel.x,
      y: posPixel.y,
    },
    righttop: {
      x: posPixel.x,
      y: posPixel.y - el.offsetHeight,
    },
    top: {
      x: posPixel.x - el.offsetWidth / 2,
      y: posPixel.y - el.offsetHeight,
    },
    topleft: {
      x: posPixel.x - el.offsetWidth,
      y: posPixel.y - el.offsetHeight,
    },
    topright: {
      x: posPixel.x,
      y: posPixel.y - el.offsetHeight,
    },
  }

  return new google.maps.Point(alignments[alignment].x, alignments[alignment].y)
}

/**
 * Converts latitude and longitude coordinates from decimal degrees (DD)
 * to degrees, minutes, and seconds (DMS) format.
 */
export function convertDecimalDegreesToDMS(
  latitude: number,
  longitude: number,
) {
  const latDegrees = Math.floor(latitude)
  const latMinutes = Math.floor((latitude - latDegrees) * 60)
  const latSeconds = ((latitude - latDegrees) * 60 - latMinutes) * 60
  const latSuffix = latitude >= 0 ? 'N' : 'S'

  const lngDegrees = Math.floor(longitude)
  const lngMinutes = Math.floor((longitude - lngDegrees) * 60)
  const lngSeconds = ((longitude - lngDegrees) * 60 - lngMinutes) * 60
  const lngSuffix = longitude >= 0 ? 'E' : 'W'

  const latDMS = `${latDegrees}° ${latMinutes}' ${latSeconds.toFixed(1)}" ${latSuffix}`
  const lngDMS = `${lngDegrees}° ${lngMinutes}' ${lngSeconds.toFixed(1)}" ${lngSuffix}`

  return { lat: latDMS, lng: lngDMS }
}

/**
 * Converts a decimal number with 4 decimal places to a decimal number with 6 decimal places if needed.
 * @param {number} number - The decimal number with 4 decimal places.
 * @returns {number} - The decimal number with 6 decimal places, or the original number if it already has 6 decimal places.
 */
export function convertToSixDecimalPlaces(number: number) {
  const decimalString = number.toString()
  const decimalParts = decimalString.split('.')

  if (decimalParts.length === 2 && decimalParts[1].length === 4) {
    return parseFloat(`${decimalString}00`)
  }

  return number
}
